"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";
import type { Message } from "@/lib/core/crm/types";
import { useConnectionSignatures } from "@/lib/hooks/api/emails/email-signatures";
import { useComposeClientContext } from "@/lib/contexts/compose/ComposeClientContext";
import { useComposeRecipientsContext } from "@/lib/contexts/compose/ComposeRecipientsContext";
import { useVariableContext } from "../contexts/VariableContext";
import { hasTemplateVariables } from "@/lib/utils/crm/template-variables";
import type { ComposeFormValues } from "@/lib/types/compose-modal-types";
import type { EmailRecipient } from "../ClientEmailSelector";
import { COMPANY_PARTNERS_EMAILS } from "./compose-form-constants";

interface UseComposeFormFieldsStateOptions {
  form: UseFormReturn<ComposeFormValues>;
  originalMessage?: Message | null;
}

export function useComposeFormFieldsState({
  form,
  originalMessage,
}: UseComposeFormFieldsStateOptions) {
  const { setValue: setVariableValue, values: variableValues } = useVariableContext();
  const clientState = useComposeClientContext();
  const recipientsState = useComposeRecipientsContext();
  const { selectedClient, existingContact, personalizationOrigin } = clientState;
  const { bccRecipients, ccRecipients, setBccRecipients } = recipientsState;

  const [bccCompanyPartners, setBccCompanyPartners] = useState(false);
  const [showMoreRecipients, setShowMoreRecipients] = useState(false);
  const [showSendOptions, setShowSendOptions] = useState(false);
  const [showPersonalization, setShowPersonalization] = useState(false);

  const hasReplyRestrictions = Boolean(originalMessage);
  // `useWatch` (not `form.watch`) so the subscription lands on the COMPONENT
  // that renders these values. `form.watch(...)` re-renders the `useForm` host,
  // which the memo on `ComposeFormTabs` deliberately stops short of here.
  const control = form.control;
  const watchedFromAccount = useWatch({ control, name: "fromAccount" });
  const watchedSubject = useWatch({ control, name: "subject" });
  const watchedBody = useWatch({ control, name: "body" });
  const watchedSignature = useWatch({ control, name: "signature" });
  const { data: connectionSignatures = [] } = useConnectionSignatures(watchedFromAccount || null);
  const selectedSignature = connectionSignatures.find((sig) => sig.id === watchedSignature);
  const hasTemplateFields =
    hasTemplateVariables(watchedSubject || "") || hasTemplateVariables(watchedBody || "");

  const personalizationReplyLinked = Boolean(
    originalMessage &&
    existingContact?.id &&
    selectedClient?.id &&
    existingContact.id === selectedClient.id
  );

  const personalizationTriggerSubtitle = useMemo(() => {
    if (!selectedClient) {
      return "Pick a CRM contact when your subject or body use {{ variables }}.";
    }
    if (personalizationReplyLinked) {
      return "Linked to this reply — expand if you want a different template context.";
    }
    switch (personalizationOrigin) {
      case "auto_from_to":
        return "Automatically using your lone To recipient for variables.";
      case "manual_selection":
        return "You're using the client chosen below.";
      case "opened_with_contact":
        return "Compose was opened with this contact for personalization.";
      default:
        return "Using this client so template placeholders can resolve.";
    }
  }, [selectedClient, personalizationReplyLinked, personalizationOrigin]);

  const handleAttachmentsChange = useCallback(
    (newAttachments: ComposeFormValues["attachments"] = []) => {
      form.setValue("attachments", newAttachments);
    },
    [form]
  );

  const handleBccCompanyPartnersChange = useCallback(
    (checked: boolean) => {
      setBccCompanyPartners(checked);

      if (checked) {
        const partnerRecipients: EmailRecipient[] = COMPANY_PARTNERS_EMAILS.map((email, index) => ({
          id: `partner-${index}`,
          type: "email" as const,
          email,
        }));
        const existingEmails = new Set(bccRecipients.map((r) => r.email));
        const newPartners = partnerRecipients.filter((r) => !existingEmails.has(r.email));
        const updatedBccRecipients = [...bccRecipients, ...newPartners];

        setBccRecipients(updatedBccRecipients);
        form.setValue("bcc", updatedBccRecipients.map((r) => r.email).join(", "));
        return;
      }

      const updatedBccRecipients = bccRecipients.filter(
        (r) => !COMPANY_PARTNERS_EMAILS.includes(r.email)
      );
      setBccRecipients(updatedBccRecipients);
      form.setValue("bcc", updatedBccRecipients.map((r) => r.email).join(", "));
    },
    [bccRecipients, setBccRecipients, form]
  );

  useEffect(() => {
    const hasAllPartners = COMPANY_PARTNERS_EMAILS.every((email) =>
      bccRecipients.some((r) => r.email === email)
    );
    setBccCompanyPartners(hasAllPartners);
  }, [bccRecipients]);

  useEffect(() => {
    if (ccRecipients.length > 0 || bccRecipients.length > 0) {
      setShowMoreRecipients(true);
    }
  }, [ccRecipients, bccRecipients]);

  useEffect(() => {
    if (!watchedSignature) return;

    const signature = connectionSignatures.find((sig) => sig.id === watchedSignature);
    if (!signature?.content) return;

    if (variableValues.signature !== signature.content) {
      setVariableValue("signature", signature.content);
    }
  }, [watchedSignature, connectionSignatures, variableValues.signature, setVariableValue]);

  return {
    bccCompanyPartners,
    clientState,
    connectionSignatures,
    handleAttachmentsChange,
    handleBccCompanyPartnersChange,
    hasReplyRestrictions,
    hasTemplateFields,
    personalizationReplyLinked,
    personalizationTriggerSubtitle,
    recipientsState,
    selectedSignature,
    setShowMoreRecipients,
    setShowPersonalization,
    setShowSendOptions,
    setVariableValue,
    showMoreRecipients,
    showPersonalization,
    showSendOptions,
    watchedFromAccount,
  };
}
